// Admin-client mirror of server/src/utils/cryptoEnvelope.js. Same envelope
// shape and HKDF/AES-GCM/HMAC primitives as the server and the web SPA.
// Uses platform WebCrypto — no third-party dependency.

const KID = 'v1';
const SALT_STR = 'vehiclesetu.envelope.v1';
const INFO_AES_STR = 'vs.enc.v1.aes';
const INFO_HMAC_STR = 'vs.enc.v1.hmac';
const IV_BYTES = 12;
const MAX_SKEW_SEC = 60;

function utf8(s) { return new TextEncoder().encode(s); }
function fromUtf8(buf) { return new TextDecoder().decode(buf); }

function b64uEncode(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=+$/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function b64uDecode(str) {
  const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
  const bin = atob(str.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function concatBytes(...parts) {
  const len = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}

async function sha256(bytes) {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
}

async function hkdf(secret, salt, info, length) {
  const ikm = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt, info },
    ikm,
    length * 8,
  );
  return new Uint8Array(bits);
}

let cachedKeys = null;

function getSecret() {
  const raw = process.env.REACT_APP_API_ENC_SECRET || '';
  if (!raw) {
    // Dev fallback only — must match server/src/middleware/encrypted.js
    return utf8('vs-dev-only-do-not-use-in-prod-aaaaaaaaaaaaaaaaaaaaaaaa');
  }
  try {
    const b = b64uDecode(raw);
    if (b.length >= 16) return b;
  } catch (_) { /* ignore */ }
  return utf8(raw);
}

async function importKeys() {
  if (!cachedKeys) {
    cachedKeys = (async () => {
      const secret = getSecret();
      const salt = await sha256(utf8(SALT_STR));
      const aesRaw = await hkdf(secret, salt, utf8(INFO_AES_STR), 32);
      const hmacRaw = await hkdf(secret, salt, utf8(INFO_HMAC_STR), 32);
      const [aesKey, hmacKey] = await Promise.all([
        crypto.subtle.importKey('raw', aesRaw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']),
        crypto.subtle.importKey('raw', hmacRaw, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']),
      ]);
      return { aesKey, hmacKey };
    })();
  }
  return cachedKeys;
}

export async function encryptEnvelope(plaintextObj) {
  const { aesKey, hmacKey } = await importKeys();
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const plaintext = utf8(JSON.stringify(plaintextObj == null ? null : plaintextObj));
  const ctBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext);
  const ct = new Uint8Array(ctBuf);
  const ts = Math.floor(Date.now() / 1000);
  const sigBuf = await crypto.subtle.sign('HMAC', hmacKey, concatBytes(iv, ct, utf8(String(ts))));
  return {
    iv: b64uEncode(iv),
    ct: b64uEncode(ct),
    ts,
    sig: b64uEncode(new Uint8Array(sigBuf)),
  };
}

export async function decryptEnvelope(env) {
  const { aesKey, hmacKey } = await importKeys();
  const iv = b64uDecode(env.iv);
  const ct = b64uDecode(env.ct);
  const sig = b64uDecode(env.sig);
  const ts = typeof env.ts === 'string' ? parseInt(env.ts, 10) : env.ts;
  if (Math.abs(Math.floor(Date.now() / 1000) - ts) > MAX_SKEW_SEC) {
    throw new Error('stale envelope');
  }
  const expected = new Uint8Array(
    await crypto.subtle.sign('HMAC', hmacKey, concatBytes(iv, ct, utf8(String(ts)))),
  );
  if (sig.length !== expected.length) throw new Error('bad sig');
  let diff = 0;
  for (let i = 0; i < sig.length; i++) diff |= sig[i] ^ expected[i];
  if (diff !== 0) throw new Error('bad sig');
  const ptBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ct);
  return JSON.parse(fromUtf8(ptBuf));
}

export function isEnvelope(x) {
  if (!x || typeof x !== 'object') return false;
  return typeof x.iv === 'string' && typeof x.ct === 'string'
    && typeof x.sig === 'string' && (typeof x.ts === 'number' || typeof x.ts === 'string');
}

export const ENC_HEADERS = { 'X-Enc': '1', 'X-Enc-Kid': KID };
